import { createFileRoute } from '@tanstack/react-router'
import { CoachingPage } from '@/components/content/content-layout'
import {
  SITE_NAME,
  SITE_URL,
  breadcrumbSchema,
  canonicalUrl,
  seo,
} from '@/shared/lib/seo'
import { BOOKING_HREF, REVIEW } from '@/content/offer'

// This is the page people paste into a YouTube description or a Discord, so
// it needs its own title and card rather than inheriting the homepage's.
const TITLE = `Workflow Review — ${REVIEW.price}, ${REVIEW.duration}, 1:1 | ControlStack.ai`
const DESCRIPTION = `Bring a codebase, a plan, or an agent setup that keeps going wrong. ${REVIEW.duration}, one to one, on a screen-share. You leave with a written plan. ${REVIEW.price}.`

/**
 * The paid offer, as structured data. Price, currency and duration are read
 * from `@/content/offer` so the markup cannot drift away from the page copy.
 *
 * Deliberately no `aggregateRating` or `review` — there are no delivered
 * sessions to rate yet, and inventing them is both a policy violation and a
 * manual-action risk. Add it once real session feedback exists.
 */
const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${canonicalUrl('/call')}#service`,
  name: REVIEW.name,
  serviceType: 'AI coding workflow consulting',
  description: DESCRIPTION,
  url: canonicalUrl('/call'),
  // organizationSchema itself is emitted only on the homepage, and JSON-LD
  // @id references resolve within a single page's graph — Google does not
  // stitch nodes across URLs. A bare @id here would give `provider` an
  // identifier and nothing else, so repeat the minimum that makes it readable
  // while keeping the @id so the two nodes still merge into one entity.
  provider: {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
  },
  audience: {
    '@type': 'Audience',
    audienceType:
      'Software engineers and technical founders using AI coding agents',
  },
  termsOfService: `${SITE_URL}/terms`,
  offers: {
    '@type': 'Offer',
    price: REVIEW.priceAmount,
    priceCurrency: REVIEW.priceCurrency,
    // Two sessions a week is a real cap, not scarcity theatre.
    availability: 'https://schema.org/LimitedAvailability',
    url: BOOKING_HREF,
    category: 'Consulting',
  },
}

export const Route = createFileRoute('/call')({
  head: () => ({
    ...seo({
      title: TITLE,
      description: DESCRIPTION,
      path: '/call',
      jsonLd: [serviceSchema, breadcrumbSchema(REVIEW.name, '/call')],
    }),
  }),
  component: CoachingPage,
})
